/**
 * All money in the system is an integer number of pence (GBP, VAT included).
 * Never use floating-point pounds for arithmetic.
 */
export type Pence = number;

export class MoneyError extends Error {
  override name = 'MoneyError';
}

/** Throws unless `p` is a safe, non-negative integer. */
export function assertPence(p: unknown, label = 'amount'): asserts p is Pence {
  if (typeof p !== 'number' || !Number.isSafeInteger(p) || p < 0) {
    throw new MoneyError(`${label} must be a non-negative whole number of pence, got ${String(p)}`);
  }
}

/** £9.95 -> 995. Only for turning human input or seed data into pence. */
export function toPence(pounds: number): Pence {
  if (!Number.isFinite(pounds) || pounds < 0) throw new MoneyError(`cannot convert ${pounds} to pence`);
  return Math.round(pounds * 100);
}

/** 995 -> "£9.95". 1000 -> "£10.00". */
export function formatPence(p: Pence): string {
  assertPence(p);
  const pounds = Math.floor(p / 100);
  const pence = p % 100;
  return `£${pounds.toLocaleString('en-GB')}.${String(pence).padStart(2, '0')}`;
}

/** Sum a list of pence amounts safely. */
export function sumPence(values: readonly Pence[]): Pence {
  return values.reduce<Pence>((acc, v) => {
    assertPence(v);
    return acc + v;
  }, 0);
}
